import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Target, Users, Shield, Zap, Heart, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const teamMembers = [
  {
    name: 'Sarah Johnson',
    role: 'CEO & Founder',
    image: 'https://images.unsplash.com/photo-1629507313712-f21468afdf2e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmZW1hbGUlMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDF8fHx8MTc2OTQ4MzEzM3ww&ixlib=rb-4.1.0&q=80&w=1080',
  },
  {
    name: 'Michael Chen',
    role: 'CTO',
    image: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBlcnNvbiUyMGhlYWRzaG90JTIwcHJvZmVzc2lvbmFsfGVufDF8fHx8MTc2OTQ5MTk5MXww&ixlib=rb-4.1.0&q=80&w=1080',
  },
  {
    name: 'Emily Rodriguez',
    role: 'Head of Product',
    image: 'https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjBwcm9mZXNzaW9uYWxzJTIwcG9ydHJhaXR8ZW58MXx8fHwxNzY5NDkxOTkxfDA&ixlib=rb-4.1.0&q=80&w=1080',
  },
  {
    name: 'David Kim',
    role: 'Head of Marketing',
    image: 'https://images.unsplash.com/photo-1758518732175-5d608ba3abdf?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZWFtJTIwbWVldGluZyUyMGJ1c2luZXNzfGVufDF8fHx8MTc2OTQ5MTk5MXww&ixlib=rb-4.1.0&q=80&w=1080',
  },
];

const values = [
  {
    icon: Target,
    title: 'Customer First',
    description: 'We put our customers at the heart of everything we do, ensuring their success is our success.',
    color: 'bg-blue-100 text-blue-600',
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: 'We continuously evolve and innovate to provide cutting-edge solutions for modern businesses.',
    color: 'bg-purple-100 text-purple-600',
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We operate with transparency, honesty, and ethical practices in all our business dealings.',
    color: 'bg-green-100 text-green-600',
  },
  {
    icon: Heart,
    title: 'Passion',
    description: 'We are passionate about helping businesses grow and succeed through better communication.',
    color: 'bg-red-100 text-red-600',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We believe in the power of teamwork and building strong relationships with our partners.',
    color: 'bg-yellow-100 text-yellow-600',
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'We serve businesses worldwide, breaking down communication barriers across borders.',
    color: 'bg-indigo-100 text-indigo-600',
  },
];

export function AboutPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="about-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E8F9EE] to-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">About GiriChat</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            We're on a mission to revolutionize how businesses communicate with their customers through WhatsApp marketing.
          </p>
        </div>
      </div>

      {/* Our Mission */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                At GiriChat, we believe that effective communication is the cornerstone of business success. Our mission is to empower businesses of all sizes with powerful, easy-to-use WhatsApp marketing tools that drive growth and engagement.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Since our founding in 2020, we've helped over 10,000 businesses across 150+ countries transform their customer communication strategies. From small startups to enterprise organizations, we provide the tools and support needed to succeed in today's digital-first world.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We're committed to continuous innovation, exceptional customer service, and building products that make a real difference in how businesses connect with their audiences.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#E8F9EE] to-[#D4F4E0] rounded-2xl p-12 shadow-lg">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-900 mb-2">10K+</div>
                    <div className="text-sm text-gray-700">Businesses Served</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-900 mb-2">150+</div>
                    <div className="text-sm text-gray-700">Countries</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-900 mb-2">500M+</div>
                    <div className="text-sm text-gray-700">Messages Sent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-900 mb-2">98%</div>
                    <div className="text-sm text-gray-700">Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The Team */}
      <div className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet The Team</h2>
            <p className="text-xl text-gray-600">
              The passionate people behind GiriChat
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <Card key={index} className="overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow bg-white">
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-sm text-gray-600">{member.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Our Values */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="p-6 border border-gray-200 hover:shadow-lg transition-shadow bg-white">
                <div className={`w-12 h-12 rounded-lg ${value.color} flex items-center justify-center mb-4`}>
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-[#00D95F] to-[#00C054] py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Join us on our journey</h2>
          <p className="text-xl text-white/90 mb-8">
            Be part of the future of WhatsApp marketing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-[#00D95F] rounded-md hover:bg-gray-100 transition-colors font-semibold"
            >
              Get Started Free
            </Link>
            <a
              href="#"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white rounded-md hover:bg-white/10 transition-colors font-semibold"
            >
              View Careers
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
